import { useCallback, useEffect, useMemo, useState } from 'react'
import { Receipt, Plus, Download } from 'lucide-react'
import { toast } from 'sonner'
import { PageShell } from '@/components/common/PageShell'
import { useWorkspaceCompanyFilter } from '@/features/companies/components/WorkspaceCompanyFilter'
import {
  useExpensesWorkspace,
  useDeleteExpense,
} from '@/features/expenses/hooks/useExpenses'
import { exportExpensesExcel, fetchExpenseAttachment } from '@/api/services/expenses.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Button, ConfirmDialog } from '@/components/ui'
import { FilePreviewModal, type PreviewFile } from '@/components/common/FilePreviewModal'
import { usePermissions } from '@/hooks/usePermissions'
import type {
  ExpenseAttachment,
  ExpenseEntry,
  ExpenseWorkspaceQuery,
} from '@/features/expenses/types/expense.types'
import { ExpenseTable, type ExpenseFilters } from './ExpenseTable'
import { ExpenseFormModal } from './ExpenseFormModal'
import { ExpenseScopeFilter } from './ExpenseScopeFilter'
import { scopedCompanyIds, useExpenseScope } from './expenseScope'
import { useOpenFromQuery } from '@/hooks/useOpenFromQuery'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

const EMPTY_FILTERS: ExpenseFilters = {
  search: '',
  category: '',
  dateFrom: '',
  dateTo: '',
  amountMin: '',
  amountMax: '',
}

function toPreviewFile(attachment: ExpenseAttachment, index: number): PreviewFile {
  return {
    id: attachment.id,
    name: attachment.originalName || `Receipt ${index + 1}`,
    mimeType: attachment.mimeType,
    size: attachment.size,
  }
}

export function WorkspaceExpensesPage(): React.ReactElement {
  const { can } = usePermissions()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [filters, setFilters] = useState<ExpenseFilters>(EMPTY_FILTERS)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const resetPage = useCallback(() => setPage(1), [])
  const filter = useWorkspaceCompanyFilter({ onChange: resetPage })
  const scope = useExpenseScope(resetPage)

  const [formOpen, setFormOpen] = useState(false)
  useOpenFromQuery(setFormOpen)
  const [editing, setEditing] = useState<ExpenseEntry | null>(null)
  const [deleting, setDeleting] = useState<ExpenseEntry | null>(null)
  const [viewingReceipts, setViewingReceipts] = useState<ExpenseEntry | null>(null)
  const deleteMutation = useDeleteExpense()
  const [exporting, setExporting] = useState(false)

  // Debounce the free-text search to avoid a request per keystroke.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 300)
    return () => window.clearTimeout(id)
  }, [filters.search])

  const handleFiltersChange = useCallback((patch: Partial<ExpenseFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }, [])

  const query = useMemo<ExpenseWorkspaceQuery>(
    () => ({
      page,
      limit,
      scope: scope.scope,
      companyIds: scopedCompanyIds(scope.scope, filter.companyIds),
      search: debouncedSearch || undefined,
      category: filters.category || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      amountMin: filters.amountMin ? Number(filters.amountMin) : undefined,
      amountMax: filters.amountMax ? Number(filters.amountMax) : undefined,
    }),
    [
      page,
      limit,
      filter.companyIds,
      scope.scope,
      debouncedSearch,
      filters.category,
      filters.dateFrom,
      filters.dateTo,
      filters.amountMin,
      filters.amountMax,
    ],
  )

  const listQuery = useExpensesWorkspace(query)
  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data?.data])
  const total = listQuery.data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const canCreate = can('expenses', 'create')
  // New expenses start as the kind in view: an organization expense under
  // "Organization", otherwise a company expense on the one company in view
  // (if there is exactly one). Both can be changed in the form.
  const selectedCompanyIds = Array.from(filter.selectedCompanyIds)
  const createDefaults =
    scope.scope === 'organization'
      ? { type: 'organization' as const, companyId: null }
      : {
          type: 'company' as const,
          companyId:
            scope.scope === 'company' && selectedCompanyIds.length === 1
              ? selectedCompanyIds[0]
              : filter.companies.length === 1
                ? filter.companies[0]._id
                : null,
        }

  const openCreate = (): void => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (entry: ExpenseEntry): void => {
    setEditing(entry)
    setFormOpen(true)
  }

  const receiptFiles = useMemo<PreviewFile[]>(
    () => (viewingReceipts?.attachments ?? []).map(toPreviewFile),
    [viewingReceipts],
  )
  const viewingExpenseId = viewingReceipts?._id
  const loadReceipt = useCallback(
    (file: PreviewFile) => fetchExpenseAttachment(viewingExpenseId ?? '', file.id),
    [viewingExpenseId],
  )

  const confirmDelete = (): void => {
    if (!deleting) return
    deleteMutation.mutate(
      { companyId: deleting.companyId ?? null, id: deleting._id },
      { onSuccess: () => setDeleting(null) },
    )
  }

  const handleExport = async (): Promise<void> => {
    setExporting(true)
    try {
      // Export every matching row (drop pagination) but keep all active filters.
      const exportQuery: ExpenseWorkspaceQuery = { ...query, page: undefined, limit: undefined }
      const blob = await exportExpensesExcel(exportQuery)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `expenses-${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success('Excel export downloaded')
    } catch (err) {
      toast.error(getErrorMessage(err))
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <PageShell
        breadcrumbItems={[{ label: 'Expenses' }]}
        icon={Receipt}
        title="Expenses"
        description="Track and manage organization and company expenses."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={<ExpenseScopeFilter scope={scope} companies={filter} />}
            actions={
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleExport()}
                  disabled={total === 0}
                  loading={exporting}
                  title={
                    total === 0 ? 'No expenses to export' : 'Download filtered expenses as Excel'
                  }
                >
                  <Download className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Export Excel</span>
                </Button>
                {canCreate ? (
                  <Button type="button" variant="primary" size="sm" onClick={openCreate}>
                    <Plus className="h-4 w-4" aria-hidden />
                    <span className="ml-1.5">Add expense</span>
                  </Button>
                ) : null}
              </>
            }
          />

          <ExpenseTable
            entries={rows}
            isLoading={listQuery.isLoading}
            error={listQuery.error as Error | null}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onRetry={() => listQuery.refetch()}
            onEdit={openEdit}
            onDelete={setDeleting}
            onViewReceipts={setViewingReceipts}
            companyNameById={filter.companyNameById}
          />

          <TablePaginationBar
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      </PageShell>

      {formOpen && (
        <ExpenseFormModal
          key={editing?._id ?? `new:${createDefaults.type}:${createDefaults.companyId ?? ''}`}
          companies={filter.companies}
          defaults={createDefaults}
          open={formOpen}
          expense={editing}
          onClose={() => {
            setFormOpen(false)
            setEditing(null)
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete expense"
        message={
          deleting
            ? `Delete "${deleting.title}"? This action cannot be undone.`
            : 'Delete this expense?'
        }
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteMutation.isPending}
      />

      <FilePreviewModal
        open={!!viewingReceipts}
        onClose={() => setViewingReceipts(null)}
        title={viewingReceipts ? `Receipts — ${viewingReceipts.title}` : 'Receipts'}
        subtitle={`${receiptFiles.length.toLocaleString()} receipt${receiptFiles.length === 1 ? '' : 's'}`}
        files={receiptFiles}
        loadFile={loadReceipt}
      />
    </>
  )
}

WorkspaceExpensesPage.displayName = 'WorkspaceExpensesPage'
